// dashboard-selector.jsx
import React, { useState, useEffect } from "react";
import * as bootstrap from "bootstrap";
import useDashboardStore from "../../stores/dashboard-store";
import { useAvailableDates } from "../../hooks/useDashboard";

const DASHBOARD_SELECTORS = ['network', 'dataset', 'year', 'timePeriod', 'subnet'];

const NETWORK_OPTIONS = [
  { value: "", label: "Select Network" },
  { value: "rdco", label: "RDCO Network" },
];

const DATASET_OPTIONS = [
  { value: "", label: "Select Dataset" },
  { value: "segments", label: "Segments" },
  { value: "intersections", label: "Intersections" },
];

const TIME_PERIOD_OPTIONS = [
  { value: "", label: "Select Time Period" },
  { value: "all", label: "All Day (24 hours)" },
  { value: "em", label: "Early Morning (12:00-07:00)" },
  { value: "am", label: "AM Peak (07:00-09:00)" },
  { value: "md", label: "Mid-Day (09:00-16:00)" },
  { value: "pm", label: "PM Peak (16:00-18:00)" },
  { value: "ev", label: "Evening (18:00-24:00)" },
];

const SUBNET_OPTIONS = [
  { value: "RDCO and Externals", label: "RDCO and Externals" },
  { value: "RDCO", label: "RDCO" },
  { value: "Kelowna", label: "Kelowna" },
  { value: "West Kelowna", label: "West Kelowna" },
  { value: "Lake Country", label: "Lake Country" },
  { value: "WFN", label: "Westbank First Nation" },
  { value: "Peachland", label: "Peachland" },
];

const DashboardSelector = () => {
  const reportParameters = useDashboardStore((state) => state.reportParameters);
  const reportSummary = useDashboardStore((state) => state.reportSummary);
  const setReportParameters = useDashboardStore(
    (state) => state.setReportParameters
  );
  const setNetworkOptions = useDashboardStore(
    (state) => state.setNetworkOptions
  );
  const setDatasetOptions = useDashboardStore(
    (state) => state.setDatasetOptions
  );
  const setTimePeriodOptions = useDashboardStore(
    (state) => state.setTimePeriodOptions
  );
  const setSubnetOptions = useDashboardStore(
    (state) => state.setSubnetOptions
  );

  const [formData, setFormData] = useState({
    network: "rdco",
    dataset: "",
    year: "",
    month: "",
    season: "",
    period: "",
    timePeriod: "",
    subnet: "RDCO and Externals",
  });

  const { data: availableDates = [], isLoading: loadingDates } =
    useAvailableDates(formData.network);

  // Derive year/month options from availableDates
  const yearOptions = React.useMemo(() => {
    const yearsSet = new Set(availableDates.map((d) => d.year));
    const years = Array.from(yearsSet).sort((a, b) => b - a);
    return [
      { value: "", label: "Select Year" },
      ...years.map((year) => ({ value: year, label: year })),
    ];
  }, [availableDates]);

  const monthOptions = React.useMemo(() => {
    if (!formData.year) {
      return [{ value: "", label: "Select a year first" }];
    }

    const months = availableDates
      .filter((date) => date.year.toString() === formData.year)
      .map((date) => date.month);

    const uniqueMonths = Array.from(new Set(months)).sort((a, b) => a - b);

    return [
      { value: "", label: "Select Month" },
      ...uniqueMonths.map((month) => ({
        value: month.toString().padStart(2, "0"),
        label: new Date(0, month - 1).toLocaleString("default", {
          month: "long",
        }),
      })),
    ];
  }, [availableDates, formData.year]);

  // Push stable option references to the store once on mount
  useEffect(() => {
    setNetworkOptions(NETWORK_OPTIONS);
    setDatasetOptions(DATASET_OPTIONS);
    setTimePeriodOptions(TIME_PERIOD_OPTIONS);
    setSubnetOptions(SUBNET_OPTIONS);
  }, []);

  // Bootstrap popovers
  useEffect(() => {
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );
    if (popoverTriggerList) {
      popoverTriggerList.map(function (elm) {
        new bootstrap.Popover(elm);
        return true;
      });
    }
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "year") {
      // Reset month if year changes and current month is not available
      const months = availableDates
        .filter((date) => date.year.toString() === value)
        .map((date) => date.month.toString().padStart(2, "0"));

      setFormData((prev) => ({
        ...prev,
        year: value,
        month: months.includes(prev.month) ? prev.month : "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setReportParameters(formData);
  };

  // Helper to render options
  const renderOptions = (options) => {
    return options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ));
  };

  return (
    <div className="card border-0 mb-3 overflow-hidden bg-gray-800 text-white">
      <div className="card-body">
        {/* Title */}
        <div className="row">
          <div className="col-lg-12">
            <h4 className="mb-3 text-gray-500">
              <b>REPORT CONTROLS</b>
              <span className="ms-2">
                <i
                  className="fa fa-info-circle"
                  data-bs-toggle="popover"
                  data-bs-trigger="hover"
                  data-bs-title="Report Controls"
                  data-bs-placement="top"
                  data-bs-content="Select parameters to produce a congestion report."
                ></i>
              </span>
            </h4>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-lg-12">
            <form onSubmit={handleSubmit}>
              <div className="row row-gap-3 flex-column justify-content-center align-items-end">
                {/* Network — fixed to "rdco"; hidden from UI but still submitted */}
                <input type="hidden" name="network" value={formData.network} />

                {/* Subnet */}
                {DASHBOARD_SELECTORS.includes("subnet") && (
                  <div className="form-group col-xl-12">
                    <label htmlFor="subnet">Subnet (Municipality)</label>
                    <select
                      id="subnet"
                      name="subnet"
                      value={formData.subnet}
                      onChange={handleChange}
                      className="form-select form-select-sm"
                      required
                    >
                      {renderOptions(SUBNET_OPTIONS)}
                    </select>
                  </div>
                )}

                {/* Dataset */}
                {DASHBOARD_SELECTORS.includes("dataset") && (
                  <div className="form-group col-xl-12">
                    <label htmlFor="dataset">Dataset</label>
                    <select
                      id="dataset"
                      name="dataset"
                      value={formData.dataset}
                      onChange={handleChange}
                      className="form-select form-select-sm"
                      required
                    >
                      {renderOptions(DATASET_OPTIONS)}
                    </select>
                  </div>
                )}

                {/* Year */}
                {DASHBOARD_SELECTORS.includes("year") && (
                  <div className="form-group col-xl-12">
                    <label htmlFor="year">Year</label>
                    <select
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      className="form-select form-select-sm"
                      required
                      disabled={loadingDates}
                    >
                      {renderOptions(yearOptions)}
                    </select>
                  </div>
                )}

                {/* Time Period */}
                {DASHBOARD_SELECTORS.includes("timePeriod") && (
                  <div className="form-group col-xl-12">
                    <label htmlFor="timePeriod">Time Period</label>
                    <select
                      id="timePeriod"
                      name="timePeriod"
                      value={formData.timePeriod}
                      onChange={handleChange}
                      className="form-select form-select-sm"
                      required
                    >
                      {renderOptions(TIME_PERIOD_OPTIONS)}
                    </select>
                  </div>
                )}

                {/* Submit Button */}
                <div className="form-group col-xl-12 text-center">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loadingDates}
                  >
                    {loadingDates ? (
                      <span>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading...
                      </span>
                    ) : (
                      "Generate Report"
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Current Selection Display */}
        {reportParameters?.network && (
          <div className="row mt-3">
            <div className="col-lg-12">
              <h5 className="text-gray-500">Current Report Parameters</h5>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {reportParameters.network && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Network:{" "}
                    {NETWORK_OPTIONS.find(
                      (opt) => opt.value === reportParameters.network
                    )?.label || reportParameters.network}
                  </span>
                )}
                {reportParameters.dataset && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Dataset:{" "}
                    {DATASET_OPTIONS.find(
                      (opt) => opt.value === reportParameters.dataset
                    )?.label || reportParameters.dataset}
                  </span>
                )}
                {reportParameters.year && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Year: {reportParameters.year}
                  </span>
                )}
                {reportParameters.timePeriod && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Time Period:{" "}
                    {TIME_PERIOD_OPTIONS.find(
                      (opt) => opt.value === reportParameters.timePeriod
                    )?.label || reportParameters.timePeriod}
                  </span>
                )}
                {reportParameters.subnet && (
                  <span className="badge bg-orange bg-opacity-20 text-orange fw-bold fs-11px">
                    Subnet:{" "}
                    {SUBNET_OPTIONS.find(
                      (opt) => opt.value === reportParameters.subnet
                    )?.label || reportParameters.subnet}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Report Summary */}
        {reportSummary && (
          <div className="row mt-3">
            <div className="col-lg-12">
              <h5 className="text-gray-500">Report Summary</h5>
              <div className="mt-2 d-flex flex-column gap-1 fs-12px text-gray-300">
                <div>{reportSummary.report_generated_in}</div>
                <div className="d-flex justify-content-between">
                  <span>Unique Segment Geometries</span>
                  <span className="fw-bold">
                    {reportSummary.total_unique_segment_geometries?.toLocaleString()}
                    <span className="text-gray-500 fw-normal"> / 700</span>
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Unique Intersection Geometries</span>
                  <span className="fw-bold">
                    {reportSummary.total_unique_intersection_geometries?.toLocaleString()}
                    <span className="text-gray-500 fw-normal"> / 103</span>
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Segment Samples</span>
                  <span className="fw-bold">{reportSummary.total_segment_samples?.toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Intersection Segment Samples</span>
                  <span className="fw-bold">{reportSummary.total_intersection_segment_samples?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardSelector;
